import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Header from "../components/Header";
import CountdownTimer from "../components/CountdownTimer";
import PaymentModal from "../components/PaymentModal";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Users, Clock, ArrowLeft, CheckCircle2, Package, Truck, ShieldCheck, CreditCard, CalendarDays } from "lucide-react";
import { usePackageTracking } from "../components/Analytics";

const conditionLabels = {
  new: { label: "Nuevo", color: "bg-green-100 text-green-700 border-green-200" },
  used: { label: "Usado", color: "bg-amber-100 text-amber-700 border-amber-200" },
  cpo: { label: "Reacondicionado (CPO)", color: "bg-blue-100 text-blue-700 border-blue-200" }
};

const statusLabels = {
  active: { label: "Activo", color: "bg-orange-100 text-orange-700 border-orange-200" },
  confirmed: { label: "Confirmado", color: "bg-green-100 text-green-700 border-green-200" },
  refunded: { label: "Reembolsado", color: "bg-red-100 text-red-700 border-red-200" },
  delivered: { label: "Entregado", color: "bg-blue-100 text-blue-700 border-blue-200" },
  cancelled: { label: "Cancelado", color: "bg-gray-100 text-gray-700 border-gray-200" }
};

export default function PackageDetail({ user, logout }) {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    fetchPackage();
  }, [packageId]);

  // Track package view for analytics
  usePackageTracking(packageId);

  const fetchPackage = async () => {
    try {
      const response = await axios.get(`${API}/packages/${packageId}`);
      setPkg(response.data);
    } catch (error) {
      console.error("Error fetching package:", error);
      toast.error("Error al cargar el paquete");
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClick = () => {
    if (!user) {
      toast.error("Debes iniciar sesión para unirte");
      navigate("/login");
      return;
    }
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header user={user} logout={logout} />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="min-h-screen bg-white">
        <Header user={user} logout={logout} />
        <div className="max-w-7xl mx-auto px-4 py-32 text-center">
          <p className="text-muted-foreground">Paquete no encontrado</p>
          <Button variant="outline" onClick={() => navigate("/")} className="mt-4">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  const progress = (pkg.current_participants / pkg.min_participants) * 100;
  const savings = pkg.original_price - pkg.group_price;
  const savingsPercent = Math.round((savings / pkg.original_price) * 100);
  const condition = conditionLabels[pkg.product_condition] || conditionLabels.new;
  const status = statusLabels[pkg.status] || statusLabels.active;
  const canJoin = pkg.status === "active" && pkg.current_participants < pkg.max_participants;
  const deliveryDays = pkg.estimated_delivery_days || 7;

  return (
    <div className="min-h-screen bg-white" data-testid="package-detail-page">
      <Header user={user} logout={logout} />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => navigate("/")}
            className="mb-6"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a ofertas
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100">
                <img 
                  src={pkg.product_image} 
                  alt={pkg.product_name}
                  className="w-full h-full object-cover"
                />
              </div>
              <Badge className={`absolute top-4 left-4 ${condition.color}`}>
                {condition.label}
              </Badge>
              <Badge className={`absolute top-4 right-4 ${status.color}`}>
                {status.label}
              </Badge>
            </div>

            {/* Product Info */}
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {pkg.product_name}
              </h1>
              
              <p className="text-muted-foreground mb-6">
                {pkg.product_description}
              </p>

              {/* Pricing */}
              <div className="mb-8">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="text-4xl font-bold text-primary" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {pkg.group_price.toFixed(2)}€
                  </span>
                  <span className="text-xl text-muted-foreground line-through">
                    {pkg.original_price.toFixed(2)}€
                  </span>
                  <Badge className="bg-orange-500 text-white">
                    -{savingsPercent}%
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Ahorras {savings.toFixed(2)}€ con la compra grupal
                </p>
              </div>

              {/* Progress Card */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  {/* Countdown */}
                  {pkg.status === "active" && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-orange-500" />
                        <span className="text-sm font-medium">Tiempo restante</span>
                      </div>
                      <CountdownTimer endDate={pkg.ends_at} />
                    </div>
                  )}

                  {/* Participants Progress */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Participantes</span>
                      </div>
                      <span className="text-sm font-medium">
                        {pkg.current_participants} / {pkg.min_participants} mínimo
                      </span>
                    </div>
                    <Progress value={Math.min(progress, 100)} className="h-3" />
                    <p className="text-xs text-muted-foreground mt-2">
                      {pkg.min_participants - pkg.current_participants > 0 
                        ? `Faltan ${pkg.min_participants - pkg.current_participants} personas para confirmar`
                        : "¡Mínimo alcanzado!"}
                    </p>
                  </div>

                  {/* Join Button - TEST MODE */}
                  {canJoin ? (
                    <div className="space-y-3">
                      <Button 
                        className="w-full h-14 text-lg rounded-full bg-orange-500 hover:bg-orange-600"
                        onClick={() => toast.info("Estamos preparando la plataforma. Las compras estarán disponibles muy pronto.")}
                        data-testid="join-package-btn"
                      >
                        Unirse por {pkg.group_price.toFixed(2)}€
                      </Button>
                      <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <span className="text-amber-600 text-sm font-medium">Modo vista previa - Compras disponibles muy pronto</span>
                      </div>
                    </div>
                  ) : (
                    <Button 
                      className="w-full h-14 text-lg rounded-full"
                      disabled
                      data-testid="join-package-btn-disabled"
                    >
                      {pkg.status === "confirmed" ? "Compra Confirmada" : 
                       pkg.status === "refunded" ? "Reembolsado" :
                       pkg.status === "delivered" ? "Entregado" :
                       "No disponible"}
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Guarantees */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <CalendarDays className="h-5 w-5 text-orange-500" />
                  <span><strong>Entrega estimada:</strong> {deliveryDays} {deliveryDays === 1 ? 'día' : 'días'} tras confirmación</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>Pago protegido hasta confirmación del grupo</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Package className="h-5 w-5 text-blue-500" />
                  <span>Producto {condition.label.toLowerCase()}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Truck className="h-5 w-5 text-primary" />
                  <span>Envío incluido en el precio</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <ShieldCheck className="h-5 w-5 text-purple-500" />
                  <span>Devolución automática si no se alcanza el mínimo</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Payment Modal */}
      <PaymentModal 
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        pkg={pkg}
        user={user}
      />
    </div>
  );
}
